import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { useState } from 'react';

import { IconCheck, IconTrash } from '@tabler/icons-react';

import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  EmptyStateView,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Tooltip,
} from '@optiro/ui';

import accessDeniedIllustration from '@/assets/illustrations/access-denied.png';
import { useCreateIntervalComment } from '@/hooks/intervalComments/useCreateIntervalComment';
import { useDeleteIntervalComment } from '@/hooks/intervalComments/useDeleteIntervalComment';
import { useIntervalComments } from '@/hooks/intervalComments/useIntervalComments';
import { useUserData } from '@/hooks/useUserData/useUserData';
import { Interval } from '@/types/types';

const TIME_FORMATTER = new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' });

type Props = {
  intervalId: Interval['id'] | null;
  taskName: string | null;
  onClose: () => void;
  withOverlay?: boolean;
  /**
   * Called when the viewer isn't authorized to see this interval's comments
   * (not signed in, or not a claimed+booked partner on the host project).
   * Renders a CTA in place of the thread instead of the usual empty/loading state.
   * Omit where this can't happen (e.g. inside the host company's own Planning view).
   */
  onUnauthorizedCta?: () => void;
};

export const BookingCommentsDrawer = ({
  intervalId,
  taskName,
  onClose,
  withOverlay = true,
  onUnauthorizedCta,
}: Props) => {
  const { t } = useTranslation('project');
  const { t: tClaim } = useTranslation('partnerClaim');
  const opened = intervalId !== null;
  const { data: comments, isLoading, error } = useIntervalComments(intervalId ?? 0);
  const { data: currentUser } = useUserData();
  const createComment = useCreateIntervalComment(intervalId ?? 0);
  const deleteComment = useDeleteIntervalComment(intervalId ?? 0);
  const [body, setBody] = useState('');

  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const reason = axios.isAxiosError(error)
    ? (error.response?.data as { reason?: string } | undefined)?.reason
    : undefined;

  // Anonymous viewers and viewers whose company has no footprint on this
  // project at all get the "claim your invite" CTA — that's the FOMO funnel.
  // A viewer who's already on the project but just isn't booked on this
  // specific interval (e.g. clicked another partner's bar) gets a plain
  // "no access" message instead — the CTA would make no sense to them.
  const isUnauthorizedCta = status === 401 || reason === 'not_on_project';
  const isForbidden = status === 403 && !isUnauthorizedCta;

  const handleSend = () => {
    const trimmed = body.trim();
    if (!trimmed || intervalId === null) return;
    createComment.mutate({ body: trimmed }, { onSuccess: () => setBody('') });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      withOverlay={withOverlay}
      title={
        <Stack gap={2}>
          <Text fw={600}>{t('samarbejde.comments_title')}</Text>
          {taskName && (
            <Text size="xs" c="dimmed">
              {taskName}
            </Text>
          )}
        </Stack>
      }
    >
      {isUnauthorizedCta ? (
        <EmptyStateView
          variant="page"
          title={tClaim('cta')}
          description={t('samarbejde.comments_unauthorized_description')}
          action={
            onUnauthorizedCta ? (
              <Button variant="outline" color="dark" onClick={onUnauthorizedCta}>
                {tClaim('cta')}
              </Button>
            ) : undefined
          }
        />
      ) : isForbidden ? (
        <EmptyStateView
          variant="page"
          title={t('samarbejde.comments_forbidden_title')}
          description={t('samarbejde.comments_forbidden_description')}
          illustration={<img src={accessDeniedIllustration} alt="" width={140} />}
        />
      ) : (
        <Stack gap="md" style={{ height: '100%' }}>
          <Stack gap="sm" style={{ flex: 1, overflow: 'auto' }}>
            {isLoading ? (
              <Group justify="center" py="lg">
                <Loader size="sm" />
              </Group>
            ) : comments && comments.length > 0 ? (
              comments.map((comment) => {
                const isOwn = currentUser?.id === comment.author_id;
                const time = TIME_FORMATTER.format(new Date(comment.created_at));

                const bubble = (
                  <Box
                    style={{
                      background: isOwn
                        ? 'var(--mantine-color-brand-1)'
                        : 'var(--mantine-color-gray-1)',
                      borderRadius: 'var(--mantine-radius-md)',
                      padding: '8px 12px',
                    }}
                  >
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.body}
                    </Text>
                  </Box>
                );

                if (isOwn) {
                  return (
                    <Group key={comment.id} justify="flex-end" align="center" gap={6} wrap="nowrap">
                      <Tooltip
                        label={
                          comment.seen_at
                            ? t('samarbejde.comments_seen')
                            : t('samarbejde.comments_not_seen_yet')
                        }
                      >
                        <ThemeIcon
                          size={16}
                          radius="xl"
                          variant={comment.seen_at ? 'filled' : 'light'}
                          color={comment.seen_at ? 'teal' : 'gray'}
                          style={{ flexShrink: 0 }}
                        >
                          {comment.seen_at ? <IconCheck size={9} /> : null}
                        </ThemeIcon>
                      </Tooltip>
                      <Stack gap={2} style={{ maxWidth: '78%', alignItems: 'flex-end' }}>
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => deleteComment.mutate(comment.id)}
                            loading={deleteComment.isPending}
                            aria-label="delete"
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                          <Text size="xs" c="dimmed">
                            {time}
                          </Text>
                        </Group>
                        {bubble}
                      </Stack>
                    </Group>
                  );
                }

                return (
                  <Group key={comment.id} align="flex-start" gap="xs" wrap="nowrap">
                    <Avatar size="sm" src={comment.author_photo_url}>
                      {(comment.author_name ?? '?').slice(0, 1)}
                    </Avatar>
                    <Stack gap={2} style={{ maxWidth: '78%', minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={500} truncate>
                          {comment.author_name ?? '—'}
                        </Text>
                        {comment.from_partner && comment.author_company_name && (
                          <Badge size="xs" variant="light" color="grape">
                            {comment.author_company_name}
                          </Badge>
                        )}
                        <Text size="xs" c="dimmed">
                          {time}
                        </Text>
                      </Group>
                      {bubble}
                    </Stack>
                  </Group>
                );
              })
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="lg">
                {t('samarbejde.comments_empty')}
              </Text>
            )}
          </Stack>

          <Stack gap="xs">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.currentTarget.value)}
              placeholder={t('samarbejde.comments_placeholder')}
              minRows={2}
              autosize
            />
            <Button
              onClick={handleSend}
              loading={createComment.isPending}
              disabled={!body.trim()}
              fullWidth
            >
              {t('samarbejde.comments_send')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
};
